"use strict";
/**
 * DENSITÀ DI GIOCO — pannello analisi m²/giocatore (@leopart/calc).
 *
 * Stesse regole di training-mix.ts: minuti PIANIFICATI e di SQUADRA; un blocco
 * multi-categoria entra INTERO se almeno una delle sue categorie ha il gruppo
 * abilitato (ADR 028: i minuti non si dividono). Rapporti e percentuali sono
 * `null`, mai `0`, quando il denominatore manca.
 *
 * I blocchi eleggibili SENZA dati campo/giocatori restano validi: vengono solo
 * esclusi dal calcolo e conteggiati in `excludedBlocks` — la UI deve mostrarlo
 * (regola 4 della spec).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.minutiPesati = minutiPesati;
exports.conGruppo = conGruppo;
exports.densitaDiGioco = densitaDiGioco;
exports.densitaGiocatori = densitaGiocatori;
const kernel_1 = require("./kernel");
const training_mix_1 = require("./training-mix");
const training_context_constants_1 = require("./training-context.constants");
/**
 * Il peso in minuti di un blocco nei calcoli densità/Mallo: il tempo effettivo
 * di gioco quando inserito, altrimenti la durata pianificata (come `techMin`
 * del prototipo).
 */
function minutiPesati(b) {
    const eff = Number(b.effectiveMinutes) || 0;
    return eff > 0 ? eff : Number(b.durationMinutes) || 0;
}
/** I blocchi con ALMENO UNA categoria fra quelle abilitate al gruppo. */
function conGruppo(blocchi, slugsAbilitati) {
    return blocchi.filter((b) => b.categoryIds.some((c) => slugsAbilitati.includes(c)));
}
/**
 * Il pannello «Densità di gioco». `null` se il periodo non ha nessun blocco
 * di gioco: il pannello non va mostrato vuoto, va detto che non c'è materia.
 *
 * `byRange[].byFlow` e `byFlowTotal` coprono SOLO i blocchi `computed` (densità
 * `d` valida, cioè quelli già contati in `byRange[].minutes`): un blocco
 * escluso dal calcolo (d == null, regola 4) non contribuisce al flusso,
 * altrimenti "Modalità complessiva" e le bande mostrate non tornerebbero —
 * stesso pattern/invariante di `densitaGiocatori` (F2):
 * `byFlowTotal === Σ byRange[].byFlow` per ciascuna modalità.
 */
function densitaDiGioco(blocchi, categorieGioco) {
    var _a, _b, _c;
    const eleggibili = conGruppo(blocchi, categorieGioco);
    if (eleggibili.length === 0)
        return null;
    const minutiPerRange = new Map(training_context_constants_1.DENSITY_RANGES.map((r) => [r.id, 0]));
    const flussoPerRange = new Map(training_context_constants_1.DENSITY_RANGES.map((r) => [r.id, new Map(training_context_constants_1.FLOW_TYPES.map((f) => [f, 0]))]));
    const flussoTotale = new Map(training_context_constants_1.FLOW_TYPES.map((f) => [f, 0]));
    let esclusi = 0;
    let pesoTotale = 0;
    let densitaPesata = 0;
    let minutiEffettivi = 0;
    let minutiPianificati = 0;
    for (const b of eleggibili) {
        minutiPianificati += b.durationMinutes;
        if (Number(b.effectiveMinutes) > 0)
            minutiEffettivi += Number(b.effectiveMinutes);
        const d = (0, training_context_constants_1.densitaDi)(b.fieldWidthM, b.fieldLengthM, b.playersOnField);
        if (d == null) {
            esclusi += 1;
            continue;
        }
        const peso = minutiPesati(b);
        const range = (0, training_context_constants_1.classificaDensita)(d);
        minutiPerRange.set(range.id, ((_a = minutiPerRange.get(range.id)) !== null && _a !== void 0 ? _a : 0) + peso);
        pesoTotale += peso;
        densitaPesata += d * peso;
        // Flusso valido SOLO se presente fra FLOW_TYPES: come densitaGiocatori,
        // un blocco senza modalità (o con valore corrotto) non contribuisce al
        // flusso ma resta comunque nei minuti della banda.
        const flusso = b.flowType && training_context_constants_1.FLOW_TYPES.includes(b.flowType) ? b.flowType : null;
        if (flusso) {
            flussoTotale.set(flusso, ((_b = flussoTotale.get(flusso)) !== null && _b !== void 0 ? _b : 0) + peso);
            const perRange = flussoPerRange.get(range.id);
            perRange.set(flusso, ((_c = perRange.get(flusso)) !== null && _c !== void 0 ? _c : 0) + peso);
        }
    }
    const media = pesoTotale > 0 ? (0, kernel_1.round)(densitaPesata / pesoTotale, 0) : null;
    return {
        eligibleBlocks: eleggibili.length,
        computedBlocks: eleggibili.length - esclusi,
        excludedBlocks: esclusi,
        byRange: training_context_constants_1.DENSITY_RANGES.map((r) => {
            var _a;
            return ({
                key: r.id,
                minutes: (_a = minutiPerRange.get(r.id)) !== null && _a !== void 0 ? _a : 0,
                byFlow: training_context_constants_1.FLOW_TYPES.map((f) => {
                    var _a, _b;
                    return ({
                        flow: f,
                        minutes: (_b = (_a = flussoPerRange.get(r.id)) === null || _a === void 0 ? void 0 : _a.get(f)) !== null && _b !== void 0 ? _b : 0,
                    });
                }),
            });
        }),
        byFlowTotal: training_context_constants_1.FLOW_TYPES.map((f) => {
            var _a;
            return ({
                flow: f,
                minutes: (_a = flussoTotale.get(f)) !== null && _a !== void 0 ? _a : 0,
            });
        }),
        weightedAvgDensity: media,
        pctOfMatchRef: media == null ? null : (0, training_mix_1.percentuale)(media, training_context_constants_1.MATCH_REF_DENSITY),
        effectiveMinutes: minutiEffettivi,
        plannedMinutes: minutiPianificati,
        effectiveRatio: (0, training_mix_1.percentuale)(minutiEffettivi, minutiPianificati),
    };
}
/**
 * Il pannello «Densità per numero di giocatori» (F2). Stesse regole di
 * `densitaDiGioco`: eleggibile = almeno una categoria col gruppo
 * 'contesto-gioco' abilitato, minuti pesati sul tempo effettivo quando
 * presente. I blocchi eleggibili senza `playersOnField` restano validi:
 * esclusi da `byGroup` ma conteggiati in `excludedBlocks`.
 *
 * `byFlowTotal` copre SOLO i blocchi `computed` (con `playersOnField` valido,
 * cioè quelli che compaiono in `byGroup`): un blocco escluso dal calcolo (nota
 * UI regola 4) non deve comparire nemmeno qui, altrimenti la "Modalità
 * complessiva" e i gruppi mostrati non tornano — invariante:
 * `byFlowTotal === Σ byGroup[].byFlow` per ciascuna modalità.
 *
 * `null` se il periodo non ha nessun blocco di gioco (pannello senza materia).
 */
function densitaGiocatori(blocchi, categorieGioco) {
    var _a, _b, _c, _d;
    const eleggibili = conGruppo(blocchi, categorieGioco);
    if (eleggibili.length === 0)
        return null;
    const minutiPerGruppo = new Map(training_context_constants_1.PLAYER_GROUPS.map((g) => [g.id, 0]));
    const blocchiPerGruppo = new Map(training_context_constants_1.PLAYER_GROUPS.map((g) => [g.id, 0]));
    const areePerGruppo = new Map(training_context_constants_1.PLAYER_GROUPS.map((g) => [g.id, []]));
    const flussoPerGruppo = new Map(training_context_constants_1.PLAYER_GROUPS.map((g) => [g.id, new Map(training_context_constants_1.FLOW_TYPES.map((f) => [f, 0]))]));
    const flussoTotale = new Map(training_context_constants_1.FLOW_TYPES.map((f) => [f, 0]));
    let esclusi = 0;
    let minutiTotali = 0;
    for (const b of eleggibili) {
        const peso = minutiPesati(b);
        // Il gruppo va classificato PRIMA di aggiornare qualunque totale: un
        // blocco senza `playersOnField` è escluso dal calcolo (regola 4) e non
        // deve contribuire a nessuna aggregazione, `byFlowTotal` incluso.
        const gruppo = (0, training_context_constants_1.classificaGruppo)(b.playersOnField);
        if (gruppo == null) {
            esclusi += 1;
            continue;
        }
        const flusso = b.flowType && training_context_constants_1.FLOW_TYPES.includes(b.flowType) ? b.flowType : null;
        if (flusso) {
            flussoTotale.set(flusso, ((_a = flussoTotale.get(flusso)) !== null && _a !== void 0 ? _a : 0) + peso);
        }
        minutiPerGruppo.set(gruppo, ((_b = minutiPerGruppo.get(gruppo)) !== null && _b !== void 0 ? _b : 0) + peso);
        blocchiPerGruppo.set(gruppo, ((_c = blocchiPerGruppo.get(gruppo)) !== null && _c !== void 0 ? _c : 0) + 1);
        minutiTotali += peso;
        const area = Number(b.fieldWidthM) > 0 && Number(b.fieldLengthM) > 0
            ? Number(b.fieldWidthM) * Number(b.fieldLengthM)
            : null;
        if (area != null)
            areePerGruppo.get(gruppo).push(area);
        if (flusso) {
            const perFlusso = flussoPerGruppo.get(gruppo);
            perFlusso.set(flusso, ((_d = perFlusso.get(flusso)) !== null && _d !== void 0 ? _d : 0) + peso);
        }
    }
    return {
        eligibleBlocks: eleggibili.length,
        computedBlocks: eleggibili.length - esclusi,
        excludedBlocks: esclusi,
        byGroup: training_context_constants_1.PLAYER_GROUPS.map((g) => {
            var _a, _b, _c, _d;
            const aree = (_a = areePerGruppo.get(g.id)) !== null && _a !== void 0 ? _a : [];
            return {
                group: g.id,
                labelIt: g.labelIt,
                minutes: (_b = minutiPerGruppo.get(g.id)) !== null && _b !== void 0 ? _b : 0,
                blocks: (_c = blocchiPerGruppo.get(g.id)) !== null && _c !== void 0 ? _c : 0,
                pct: (0, training_mix_1.percentuale)((_d = minutiPerGruppo.get(g.id)) !== null && _d !== void 0 ? _d : 0, minutiTotali),
                avgAreaM2: aree.length > 0 ? (0, kernel_1.round)((0, kernel_1.mean)(aree), 1) : null,
                byFlow: training_context_constants_1.FLOW_TYPES.map((f) => {
                    var _a, _b;
                    return ({
                        flow: f,
                        minutes: (_b = (_a = flussoPerGruppo.get(g.id)) === null || _a === void 0 ? void 0 : _a.get(f)) !== null && _b !== void 0 ? _b : 0,
                    });
                }),
            };
        }),
        byFlowTotal: training_context_constants_1.FLOW_TYPES.map((f) => {
            var _a;
            return ({
                flow: f,
                minutes: (_a = flussoTotale.get(f)) !== null && _a !== void 0 ? _a : 0,
            });
        }),
        totalMinutes: minutiTotali,
    };
}
